// @ts-nocheck
import { motion } from 'framer-motion';
import { UserAvatar } from './UserAvatar';

interface ProgressIndicatorProps {
    currentStep: number;
    onLogout?: () => void;
    onUpgrade?: () => void;
}

const steps = [
    { number: 1, label: 'Select' },
    { number: 2, label: 'Pair' },
    { number: 3, label: 'Enjoy' },
];

export default function ProgressIndicator({
    currentStep,
    onLogout,
    onUpgrade,
}: ProgressIndicatorProps) {
    return (
        <motion.div
            className="w-full px-6 pt-8 pb-10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            <div className="mx-auto max-w-2xl">
                {/* Header with Niio Logo and space for Avatar */}
                {currentStep >= 1 && (
                    <div className="relative flex items-center justify-center mb-0 h-14 pb-5">
                        {/* Niio Logo in center - absolutely positioned to stay centered regardless of avatar width */}
                        <motion.div
                            className="absolute left-1/2 flex justify-center items-center z-10"
                            initial={{ opacity: 0, y: -10, x: '-50%' }}
                            animate={{ opacity: 1, y: 0, x: '-50%' }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                        >
                            <motion.div
                                className="relative"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 1,
                                    delay: 0.1,
                                    ease: 'easeOut',
                                }}
                            >
                                {/* Purple glow background */}
                                <motion.div
                                    className="absolute inset-0 bg-purple-500/30 rounded-full blur-2xl"
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{
                                        scale: [0.8, 1, 1.05, 1],
                                        opacity: [0, 0.3, 0.4, 0.3],
                                    }}
                                    transition={{
                                        duration: 2,
                                        delay: 0.2,
                                        repeat: Infinity,
                                        repeatType: 'reverse',
                                        ease: 'easeInOut',
                                    }}
                                    style={{
                                        transform: 'translate(-50%, -50%)',
                                        top: '50%',
                                        left: '50%',
                                        width: '120%',
                                        height: '120%',
                                    }}
                                />
                                {/* Logo */}
                                <img
                                    src="/niio_logo_white.png"
                                    alt="Niio"
                                    className="relative z-10 w-32 h-12 object-contain object-center"
                                    style={{
                                        filter: 'drop-shadow(0 4px 20px rgba(139, 92, 246, 0.4)) drop-shadow(0 2px 10px rgba(139, 92, 246, 0.3))',
                                    }}
                                />
                            </motion.div>
                        </motion.div>

                        {/* User Avatar on the right - absolutely positioned with higher z-index */}
                        <div className="absolute right-0 flex items-center z-20">
                            <UserAvatar
                                onLogout={onLogout}
                                onUpgrade={onUpgrade}
                            />
                        </div>
                    </div>
                )}
                <div className="relative flex items-center justify-between">
                    {/* Background line */}
                    <div className="absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 bg-gray-700" />

                    {/* Progress line */}
                    <motion.div
                        className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 bg-gradient-to-r from-purple-500 to-purple-600"
                        initial={{ width: '0%' }}
                        animate={{
                            width: `${
                                ((currentStep - 1) / (steps.length - 1)) * 100
                            }%`,
                        }}
                        transition={{ duration: 0.8, ease: 'easeInOut' }}
                        style={{
                            boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)',
                        }}
                    />

                    {/* Steps */}
                    {steps.map((step, index) => {
                        return (
                            <motion.div
                                key={step.number}
                                className="relative z-10 flex flex-col items-center"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{
                                    delay: index * 0.2,
                                    duration: 0.4,
                                }}
                            >
                                <motion.div
                                    className={`
                  flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-300
                  ${
                      currentStep >= step.number
                          ? 'border-purple-500 bg-purple-500 text-white'
                          : 'border-gray-600 bg-black text-gray-400'
                  }
                `}
                                    animate={
                                        currentStep >= step.number
                                            ? {
                                                  boxShadow: [
                                                      '0 0 0 0px rgba(147, 51, 234, 0.4)',
                                                      '0 0 0 8px rgba(147, 51, 234, 0.1)',
                                                      '0 0 0 0px rgba(147, 51, 234, 0.4)',
                                                  ],
                                              }
                                            : {}
                                    }
                                    transition={{
                                        duration: 1.5,
                                        repeat:
                                            currentStep >= step.number
                                                ? Infinity
                                                : 0,
                                    }}
                                    whileHover={{ scale: 1.1 }}
                                >
                                    <motion.span
                                        className="text-sm font-semibold"
                                        animate={
                                            currentStep >= step.number
                                                ? {
                                                      textShadow: [
                                                          '0 0 0px #ffffff',
                                                          '0 0 8px #ffffff',
                                                          '0 0 0px #ffffff',
                                                      ],
                                                  }
                                                : {}
                                        }
                                        transition={{
                                            duration: 1.5,
                                            repeat:
                                                currentStep >= step.number
                                                    ? Infinity
                                                    : 0,
                                        }}
                                    >
                                        {step.number}
                                    </motion.span>
                                </motion.div>
                                <motion.span
                                    className={`
                  absolute -bottom-6 whitespace-nowrap text-xs font-medium transition-all duration-300
                  ${currentStep >= step.number ? 'text-white' : 'text-gray-500'}
                `}
                                    animate={
                                        currentStep >= step.number
                                            ? {
                                                  textShadow: [
                                                      '0 0 0px #8b5cf6',
                                                      '0 0 4px #8b5cf6',
                                                      '0 0 0px #8b5cf6',
                                                  ],
                                              }
                                            : {}
                                    }
                                    transition={{
                                        duration: 1.5,
                                        repeat:
                                            currentStep >= step.number
                                                ? Infinity
                                                : 0,
                                    }}
                                >
                                    {step.label}
                                </motion.span>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
}
